if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('[data-quick-add-content]');
      this.loadingView = this.querySelector('[data-quick-add-loading]');
      this.status = this.querySelector('[data-quick-add-status]');
      this.error = this.querySelector('[data-quick-add-error]');
      this.retry = this.querySelector('[data-quick-add-retry]');
      this.cartLink = this.querySelector('[data-quick-add-cart]');
      this.openVersion = 0;
      this.onTrigger = (event) => {
        const trigger = event.target.closest('[data-quick-add]');
        if (!trigger || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(trigger);
      };
      document.addEventListener('click', this.onTrigger);
      this.querySelector('[data-quick-add-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        this.openVersion += 1;
        this.loadController?.abort();
        if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = [...this.dialog.querySelectorAll('button:not([disabled]), a[href], input:not([type="hidden"]):not([disabled])')].filter((element) => {
          if (element.closest('[hidden], [inert]') || !element.getClientRects().length) return false;
          if (element.type !== 'radio' || element.checked) return true;
          return ![...this.dialog.querySelectorAll('input[type="radio"]')].some((radio) => radio.name === element.name && radio.checked);
        });
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      });
      this.retry.addEventListener('click', () => this.load(this.requestUrl));
      this.content.addEventListener('change', (event) => {
        const input = event.target.closest('[data-quick-add-option]');
        if (!input) return;
        const product = this.content.querySelector('[data-quick-add-product]');
        const url = new URL(input.dataset.productUrl || product.dataset.productUrl, window.location.origin);
        const values = [...this.content.querySelectorAll('[data-quick-add-option]:checked')].map((option) => option.value);
        url.searchParams.set('option_values', values.join(','));
        this.load(url, { name: input.name, value: input.value });
      });
      this.content.addEventListener('submit', (event) => {
        if (!event.target.matches('.quick-add__form')) return;
        event.preventDefault();
        this.addToCart(event.target);
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTrigger);
      this.loadController?.abort();
      this.initialized = false;
    }

    open(trigger) {
      this.openVersion += 1;
      this.trigger = trigger;
      this.content.replaceChildren();
      this.querySelector('#QuickAddTitle').textContent = trigger.closest('.product-card')?.querySelector('.product-card__title')?.textContent || trigger.getAttribute('aria-label');
      if (!this.dialog.open) this.dialog.showModal();
      this.dialog.scrollTop = 0;
      this.load(new URL(trigger.href));
    }

    async load(productUrl, focusOption) {
      this.loadController?.abort();
      const controller = new AbortController();
      this.loadController = controller;
      const version = this.openVersion;
      const url = new URL(productUrl);
      if (url.origin !== window.location.origin) return;
      url.searchParams.set('section_id', 'quick-add-product');
      this.requestUrl = url;
      this.ready = false;
      this.added = false;
      this.loading = true;
      this.clearFeedback();
      this.loadingView.hidden = this.content.childElementCount > 0;
      if (this.loadingView.hidden) this.status.textContent = this.dataset.loadingText;
      this.updateBusyState();
      const timeout = window.setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(this.dataset.loadError);
        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const product = documentFragment.querySelector('[data-quick-add-product]');
        if (!product) throw new Error(this.dataset.loadError);
        if (version !== this.openVersion || controller !== this.loadController || !this.dialog.open) return;
        const scrollTop = this.dialog.scrollTop;
        this.content.replaceChildren(product);
        this.querySelector('#QuickAddTitle').textContent = product.querySelector('[data-quick-add-title]').textContent;
        this.ready = true;
        this.status.textContent = '';
        this.dialog.scrollTop = scrollTop;
        if (focusOption) {
          const selected = [...product.querySelectorAll('[data-quick-add-option]')].find((input) => input.name === focusOption.name && input.value === focusOption.value && !input.disabled);
          // The new content is made interactive in finally before restoring focus.
          this.optionToFocus = selected;
        }
      } catch {
        if (version === this.openVersion && controller === this.loadController && this.dialog.open) {
          this.status.textContent = '';
          this.error.textContent = this.dataset.loadError;
          this.retry.hidden = false;
        }
      } finally {
        window.clearTimeout(timeout);
        if (version === this.openVersion && controller === this.loadController) {
          this.loading = false;
          this.loadingView.hidden = true;
          this.updateBusyState();
          this.optionToFocus?.focus({ preventScroll: true });
          this.optionToFocus = null;
        }
      }
    }

    clearFeedback() {
      this.status.textContent = '';
      this.error.textContent = '';
      this.retry.hidden = true;
      this.cartLink.hidden = true;
    }

    updateBusyState() {
      const busy = Boolean(this.loading || this.submitting);
      this.content.inert = busy;
      this.content.setAttribute('aria-busy', String(busy));
      const button = this.content.querySelector('[data-quick-add-submit]');
      if (!button) return;
      const variant = this.content.querySelector('input[name="id"]');
      button.disabled = busy || !this.ready || this.added || !variant?.value || variant.disabled;
      const label = button.querySelector('[data-quick-add-submit-label]');
      if (!label.dataset.defaultLabel) label.dataset.defaultLabel = label.textContent.trim();
      label.textContent = this.submitting ? this.dataset.addingText : this.added ? this.dataset.addedText : label.dataset.defaultLabel;
    }

    async addToCart(form) {
      const variant = form.querySelector('input[name="id"]');
      if (!this.ready || this.loading || this.submitting || this.added || !variant?.value || variant.disabled) return;
      const version = this.openVersion;
      const sectionId = document.querySelector('[data-cart-section]')?.dataset.cartSection;
      const data = new FormData(form);
      if (sectionId) {
        data.set('sections', sectionId);
        data.set('sections_url', window.location.pathname + window.location.search);
      }
      this.submitting = true;
      this.clearFeedback();
      this.updateBusyState();
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(this.dataset.cartAddUrl, { method: 'POST', body: data, headers: { Accept: 'application/json' }, signal: controller.signal });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : this.dataset.addError);
        if (version === this.openVersion && this.dialog.open) {
          this.added = true;
          this.status.textContent = this.dataset.successText;
          this.cartLink.hidden = false;
        }
        await this.refreshCart(sectionId, result.sections?.[sectionId]);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { variantId: variant.value } }));
      } catch (error) {
        if (version === this.openVersion && this.dialog.open) {
          this.error.textContent = error.name === 'AbortError' || error instanceof TypeError ? this.dataset.addError : error.message;
        }
      } finally {
        window.clearTimeout(timeout);
        this.submitting = false;
        this.updateBusyState();
      }
    }

    async refreshCart(sectionId, html) {
      if (!sectionId) return;
      try {
        if (!html) {
          const url = new URL(this.dataset.cartUrl, window.location.origin);
          url.searchParams.set('section_id', sectionId);
          const response = await fetch(url, { signal: AbortSignal.timeout(5000) });
          if (!response.ok) return;
          html = await response.text();
        }
        const updated = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-section]');
        if (updated) document.querySelector('[data-cart-section]')?.replaceWith(updated);
      } catch {
        // Adding succeeded; a header refresh failure must not invite a duplicate add.
      }
    }
  });
}
